// src/interfaces/manager/AdminPanel.js
import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect, useRef } from 'react';
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';
import BreakdownDisplay from '../../components/common/BreakdownDisplay';
import { getLargeFormatProcessNames } from '../../utils/largeFormatFinishing';
import JobFolderView from '../../components/manager/JobFolderView';
import PrintroomControl from '../../components/manager/PrintroomControl';
import PrintlogicSuccessModal from '../../components/common/PrintlogicSuccessModal';
import PrintlogicOrderStatusModal from '../../components/common/PrintlogicOrderStatusModal';
import PrintlogicCustomerModal from '../../components/manager/PrintlogicCustomerModal';

// Helper function to get laminate type display name
const getLaminateTypeName = (laminateTypeId) => {
  switch (laminateTypeId) {
    case 'matt_lam':
      return 'Matt';
    case 'gloss_lam':
      return 'Gloss';
    case 'soft_touch_lam':
      return 'Soft Touch';
    case '250matt':
      return 'Matt (250 mic)';
    case '250gloss':
      return 'Gloss (250 mic)';
    default:
      return '';
  }
};

// Helper function to determine quote type
const getQuoteType = (quote) => {
  const firstItem = quote?.items?.[0];
  if (!firstItem) {
    // Fallback for older quotes or unexpected structure
    if (quote?.material) return 'Large Format'; // Guess based on old field
    if (quote?.paperStock) return 'Small Format'; // Guess based on old field (Added for completeness)
    return 'Unknown';
  }

  // --- START: Improved Type Detection (MOVED INSIDE) ---
  if (firstItem.productType === 'small-format' || firstItem.productType === 'small_format' || firstItem.paperStock) {
    return 'Small Format';
  }
  if (firstItem.productType === 'large-format' || firstItem.productType === 'large_format' || firstItem.material) {
    // Check for material as fallback for potentially older items without productType
    return 'Large Format';
  }
  return 'Unknown'; // Default if no type identified
};

// Helper function to determine if a quote is specifically a cut vinyl job
const isCutVinylJob = (quote) => {
  const firstItem = quote?.items?.[0];
  if (!firstItem) {
    // Check top-level job type for cut vinyl
    return quote?.jobType === 'Cut Vinyl' || quote?.jobType === 'cutvinyl';
  }

  // Check item-level job type
  return firstItem.jobType === 'Cut Vinyl' || firstItem.jobType === 'cutvinyl';
};

// Helper function to determine if a quote is in processing status (PrintLogic intermediate statuses)
const isProcessingStatus = (status) => {
  if (!status) return false;
  const processingStatuses = [
    'Waiting for Artwork',
    'In Progress',
    'Print Queue',
    'Finishing'
  ];
  return processingStatuses.includes(status);
};

// Helper function to determine if a quote is in completed status
const isCompletedStatus = (status) => {
  if (!status) return false;
  const completedStatuses = [
    'Completed',
    'Completed & Delivered',
    'Completed & Invoiced'
  ];
  return completedStatuses.includes(status);
};

// Helper function to determine if a quote is older than 30 days
const isQuoteOlderThan30Days = (createdAt) => {
  if (!createdAt) return false;

  const created = new Date(createdAt);
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  return created < thirtyDaysAgo;
};


const AdminPanel = ({ departmentHint }) => { // Accept the hint prop
  const { quotes, quotesTotal, quotesHasMore, fetchQuotesWithContext, loadMoreQuotes, updateQuoteStatus, adjustQuote, submitToPrintSystem, approveAndSubmitToPrintSystem, updateQuotePrintlogicCustomer, isLoading, error } = useQuotes(); // Added isLoading/error
  const { currentUser, loading: authLoading, hasRole, isAdmin } = useAuth(); // Get hasRole, isAdmin, and auth loading state
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [adjustedPrice, setAdjustedPrice] = useState('');
  const [printSystemId, setPrintSystemId] = useState('');
  const initialFormatFilter = departmentHint === 'large-format'
    ? 'large'
    : departmentHint === 'small-format'
      ? 'small'
      : 'all';
  const [activeFilter, setActiveFilter] = useState('pending');
  const [formatFilter, setFormatFilter] = useState(initialFormatFilter);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showFolderView, setShowFolderView] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({ orderNumber: '', quoteName: '' });
  const [showPrintlogicCustomerModal, setShowPrintlogicCustomerModal] = useState(false);
  const [selectedPrintlogicCustomer, setSelectedPrintlogicCustomer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double-clicks
  const [isGeneratingQuotation, setIsGeneratingQuotation] = useState(false);
  const [showQuotationOptions, setShowQuotationOptions] = useState(false);
  const [quotationRecipient, setQuotationRecipient] = useState('');
  const [quotationSignatory, setQuotationSignatory] = useState('');
  const [pendingPrintlogicAction, setPendingPrintlogicAction] = useState(null);
  const tableScrollRef = useRef(null);
  const tableHeightRafRef = useRef(null);
  const lastTableMaxHeightRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState('auto');

  const updateTableMaxHeight = useCallback(() => {
    const tableEl = tableScrollRef.current;
    if (!tableEl) return;
    const rect = tableEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const available = viewportHeight - rect.top - 16;
    const clamped = Math.max(available, 240);
    const nextMaxHeight = `${Math.floor(clamped)}px`;
    if (lastTableMaxHeightRef.current !== nextMaxHeight) {
      lastTableMaxHeightRef.current = nextMaxHeight;
      setTableMaxHeight(nextMaxHeight);
    }
  }, []);

  const scheduleTableHeightUpdate = useCallback(() => {
    if (tableHeightRafRef.current !== null) return;
    tableHeightRafRef.current = window.requestAnimationFrame(() => {
      tableHeightRafRef.current = null;
      updateTableMaxHeight();
    });
  }, [updateTableMaxHeight]);

  useLayoutEffect(() => {
    updateTableMaxHeight();
  }, [updateTableMaxHeight, selectedQuoteId, activeFilter, formatFilter, sourceFilter]);

  useEffect(() => {
    window.addEventListener('resize', scheduleTableHeightUpdate);
    window.addEventListener('scroll', scheduleTableHeightUpdate, { passive: true });
    return () => {
      window.removeEventListener('resize', scheduleTableHeightUpdate);
      window.removeEventListener('scroll', scheduleTableHeightUpdate);
      if (tableHeightRafRef.current !== null) {
        window.cancelAnimationFrame(tableHeightRafRef.current);
        tableHeightRafRef.current = null;
      }
    };
  }, [scheduleTableHeightUpdate]);

  // Build lookup tables for username/user_id -> origin based on known sources in the current dataset
  const originLookup = useMemo(() => {
    const usernameMap = {};
    const userIdMap = {};

    quotes.forEach((q) => {
      if (!q) return;
      const origin = q.source === 'manager' ? 'internal' : q.source === 'user' ? 'external' : null;
      if (!origin) return;
      if (q.username) {
        usernameMap[q.username.toLowerCase()] = origin;
      }
      if (q.user_id) {
        userIdMap[q.user_id] = origin;
      }
    });

    return { usernameMap, userIdMap };
  }, [quotes]);

  // Determine quote origin (external client vs internal team) using source, then username/user_id lookups
  // NOTE: Defined here (before early returns) because it's used in filteredQuotes useMemo
  const getQuoteOrigin = (quote) => {
    if (!quote) return 'external';
    if (quote.source === 'manager') return 'internal';
    if (quote.source === 'user') return 'external';

    const usernameKey = quote.username ? quote.username.toLowerCase() : null;
    if (usernameKey && originLookup.usernameMap[usernameKey]) {
      return originLookup.usernameMap[usernameKey];
    }

    if (quote.user_id && originLookup.userIdMap[quote.user_id]) {
      return originLookup.userIdMap[quote.user_id];
    }

    // Default to external to avoid hiding client quotes when uncertain
    return 'external';
  };

  // Filter quotes based on status, format, and source
  // IMPORTANT: useMemo ensures this recomputes correctly when dependencies change
  // (fixes stale closure bug where filter would get "stuck" on last selected tab)
  // NOTE: Must be defined before early returns to comply with React hooks rules
  const filteredQuotes = useMemo(() => {
    // DEBUG: Log the input values
    console.log('=== FILTER DEBUG ===');
    console.log('activeFilter:', activeFilter);
    console.log('formatFilter:', formatFilter);
    console.log('sourceFilter:', sourceFilter);
    console.log('Total quotes in array:', quotes.length);

    // DEBUG: Log unique statuses in the quotes array
    const uniqueStatuses = [...new Set(quotes.map(q => q.status))];
    console.log('Unique statuses in quotes:', uniqueStatuses);

    const result = quotes.filter(quote => {
      let statusMatch = false;

      if (activeFilter === 'all') {
        statusMatch = true;
      } else if (activeFilter === 'processing') {
        // Show quotes with PrintLogic intermediate statuses OR old 'processing' status
        statusMatch = isProcessingStatus(quote.status) || quote.status === 'processing';
      } else if (activeFilter === 'completed') {
        // Show quotes with completion statuses
        statusMatch = isCompletedStatus(quote.status);
      } else {
        // Original exact match for other statuses
        statusMatch = quote.status === activeFilter;

        // Additional filter for pending tab: exclude quotes older than 30 days
        if (activeFilter === 'pending' && statusMatch) {
          statusMatch = statusMatch && !isQuoteOlderThan30Days(quote.createdAt);
        }
      }

      // Format filter (Large/Small/All)
      const quoteType = getQuoteType(quote);
      let formatMatch = true;
      if (formatFilter === 'large') {
        formatMatch = quoteType === 'Large Format';
      } else if (formatFilter === 'small') {
        formatMatch = quoteType === 'Small Format';
      }

      // Source filter (External/Internal)
      const origin = getQuoteOrigin(quote);
      let sourceMatch = true;
      if (sourceFilter === 'external') {
        sourceMatch = origin === 'external';
      } else if (sourceFilter === 'internal') {
        sourceMatch = origin === 'internal';
      }

      return statusMatch && formatMatch && sourceMatch; // Ensure all filters are applied
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort newest first

    // DEBUG: Log the result
    console.log('Filtered result count:', result.length);
    console.log('Filtered result statuses:', [...new Set(result.map(q => q.status))]);
    console.log('=== END FILTER DEBUG ===');

    return result;
  }, [quotes, activeFilter, formatFilter, sourceFilter, originLookup]);

  // Check if user can access this panel (manager or admin)
  const canAccessPanel = currentUser && (hasRole('manager') || isAdmin());

  // Notification handler
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Auto-clear notification when quote selection changes
  React.useEffect(() => {
    setNotification(null);
  }, [selectedQuoteId]);

  // Keep format filter in sync when a department hint is provided
  useEffect(() => {
    if (departmentHint === 'large-format' && formatFilter !== 'large') {
      setFormatFilter('large');
    } else if (departmentHint === 'small-format' && formatFilter !== 'small') {
      setFormatFilter('small');
    }
  }, [departmentHint, formatFilter]);

  useEffect(() => {
    fetchQuotesWithContext('management', {
      status: activeFilter,
      source: sourceFilter,
      format: formatFilter,
      limit: 50,
      offset: 0,
    });
  }, [activeFilter, sourceFilter, formatFilter, fetchQuotesWithContext]);

  // Handle ESC key to close quote details
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && selectedQuoteId) {
        setSelectedQuoteId(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedQuoteId]);

  // Load customer from quote data when selected quote changes
  // Note: This must be defined early before any early returns (React hooks rule)
  useEffect(() => {
    const selectedQuote = selectedQuoteId ? quotes.find(q => q.id === selectedQuoteId) : null;
    if (selectedQuote && selectedQuote.printlogic_customer_id && selectedQuote.printlogic_customer_id.trim() && selectedQuote.printlogic_customer_id !== 'N/A' && selectedQuote.printlogic_customer_id !== 'NA') {
      setSelectedPrintlogicCustomer({
        customer_id: selectedQuote.printlogic_customer_id,
        customer_name: selectedQuote.printlogic_account_name || selectedQuote.customerName
      });
    } else {
      setSelectedPrintlogicCustomer(null);
    }
  }, [selectedQuoteId, quotes]);

  if (authLoading) { // Check auth loading first
    return <div className="text-center p-4">Authenticating...</div>;
  }

  // Original isLoading from useQuotes is for quotes data, not auth
  // We'll use it after the permission check.

  if (isLoading) {
    return <div className="text-center p-4">Loading quotes...</div>;
  }

  if (error) {
    // This error is from useQuotes, so it's fine here after auth checks.
    return <div className="text-center p-4 text-red-600">Error loading quotes: {error.message || error}</div>;
  }

  if (!canAccessPanel) { // Check if user has permission
    return (
      <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md text-center">
        <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
        <p className="text-gray-600 mb-4">You don't have permission to access Quote Management.</p>
        <p className="text-gray-600">Please log in with a manager or admin account.</p>
      </div>
    );
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    try {
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Format due date with visual indicators and circular gradient
  const formatDueDate = (dateString, status = '') => {
    if (!dateString) return {
      text: '-',
      textStyle: 'text-gray-400',
      gradientStyle: ''
    };

    // If job is completed, show neutral gray styling regardless of due date
    if (status && (status.toLowerCase().includes('completed') || status.toLowerCase().includes('invoiced'))) {
      const formattedDate = new Date(dateString).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      return {
        text: formattedDate,
        textStyle: 'text-gray-600 font-medium',
        gradientStyle: ''
      };
    }

    try {
      const dueDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time for accurate comparison
      dueDate.setHours(0, 0, 0, 0);

      const diffTime = dueDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const formattedDate = dueDate.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });

      // Determine styling based on days until due
      let textStyle = 'text-green-600 font-medium'; // Default green for 3+ days

      if (diffDays < 0) {
        // Overdue (past due date) - red
        textStyle = 'text-red-600 font-bold';
      } else if (diffDays === 0) {
        // Same day - red
        textStyle = 'text-red-600 font-bold';
      } else if (diffDays <= 2) {
        // Up to 2 days - amber
        textStyle = 'text-amber-600 font-bold';
      } else if (diffDays === 3) {
        // Up to 3 days - yellow
        textStyle = 'text-yellow-600 font-semibold';
      }

      return { text: formattedDate, textStyle, gradientStyle: '' };
    } catch (e) {
      return {
        text: 'Invalid Date',
        textStyle: 'text-red-500',
        gradientStyle: ''
      };
    }
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'adjusted': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-purple-100 text-purple-800';
      default: return 'bg-yellow-100 text-yellow-800'; // pending
    }
  };

  // Get type badge style
  const getTypeBadge = (type) => {
    switch (type) {
      case 'Small Format': return 'bg-blue-100 text-blue-800';
      case 'Large Format': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Open quote details
  const handleSelectQuote = (quote) => {
    setSelectedQuoteId(quote.id);
    setAdminNotes(quote.managerNotes || '');
    setAdjustedPrice((quote.totalPrice || quote.price || 0).toFixed(2)); // Use toFixed(2) for consistency
    setPrintSystemId(quote.printSystemId || '');
    setQuotationRecipient(quote.printlogic_account_name || quote.username || '');
    setQuotationSignatory('');
  };

  // Find the selected quote object from the current quotes array using the stored ID
  const selectedQuote = selectedQuoteId ? quotes.find(q => q.id === selectedQuoteId) : null;

  const handleGenerateQuotation = async () => {
    if (!selectedQuote || isGeneratingQuotation) return;

    setIsGeneratingQuotation(true);
    try {
      const params = new URLSearchParams();
      if (quotationRecipient.trim()) params.set('recipient', quotationRecipient.trim());
      if (quotationSignatory.trim()) params.set('signatory', quotationSignatory.trim());
      const queryString = params.toString();
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/quotes/${selectedQuote.id}/quotation.pdf${queryString ? `?${queryString}` : ''}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to generate quotation PDF');

      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `quotation-${selectedQuote.quoteRef || selectedQuote.id.slice(0, 8)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setNotification({ type: 'error', message: error.message });
    } finally {
      setIsGeneratingQuotation(false);
    }
  };

  // Get quote name for display
  const getQuoteName = (quote) => {
    const firstItem = quote?.items?.[0];
    return quote.quoteName || firstItem?.jobName || quote.jobName || 'Untitled Quote';
  };

  // Get client display name
  const getClientDisplayName = (quote) => {
    return quote.printlogic_account_name || quote.username || 'N/A';
  };

  // Get quote summary for display in the list
  const getQuoteSummary = (quote) => {
    const quoteType = getQuoteType(quote);
    const firstItem = quote?.items?.[0];
    const isCutVinyl = isCutVinylJob(quote);

    if (quote.items && quote.items.length > 1) {
      return `${quote.items.length} items`;
    }
    if (firstItem) {
      if (quoteType === 'Small Format') {
        const stockDisplay = firstItem.paperStockName || firstItem.paperStock || 'N/A';
        const jobType = firstItem.jobType || 'N/A';

        // Use standard size if available, otherwise fall back to dimensions
        let sizeDisplay;
        if (firstItem.standardSize) {
          sizeDisplay = firstItem.standardSize.toUpperCase();
        } else {
          sizeDisplay = firstItem.dimensions || (firstItem.width && firstItem.height ? `${firstItem.width}x${firstItem.height}mm` : 'N/A');
        }

        return `${jobType} - ${stockDisplay} (${sizeDisplay})`;
      }
      if (quoteType === 'Large Format' && !isCutVinyl) {
        const jobType = firstItem.jobType || 'Large Format';
        const dimensions = firstItem.dimensions || 'N/A';
        const material = firstItem.material || '';

        if (material) {
          return `${jobType} - ${material} (${dimensions})`;
        } else {
          return `${jobType} (${dimensions})`;
        }
      }
      if (isCutVinyl) {
        const pieceCount = firstItem.pieces?.length || 0;
        const totalItems = firstItem.quantity || 0;
        const materials = firstItem.materialNames || 'N/A';
        return `${pieceCount} piece${pieceCount !== 1 ? 's' : ''} (${totalItems} items) - ${materials}`;
      }
    }
    // Fallback for older single-item quotes or unknown
    return quote.material || quote.materialNames || 'Details unavailable';
  };

  // --- Action Handlers (Approve, Reject, Adjust, Submit) remain largely the same ---
  // Approve quote
  const handleApprove = () => {
    if (selectedQuote) {
      updateQuoteStatus(selectedQuote.id, 'approved', adminNotes);
      setSelectedQuoteId(null);
    }
  };

  // Reject quote
  const handleReject = () => {
    if (!adminNotes && selectedQuote?.status === 'pending') { // Only require notes for initial rejection
      alert('Please enter a reason for rejection in the notes.');
      return;
    }
    if (selectedQuote) {
      updateQuoteStatus(selectedQuote.id, 'rejected', adminNotes);
      setSelectedQuoteId(null);
    }
  };

  // Adjust quote price
  const handleAdjust = () => {
    if (!adminNotes) {
      alert('Please enter a note explaining the price adjustment.');
      return;
    }
    const newPrice = parseFloat(adjustedPrice);
    if (isNaN(newPrice) || newPrice <= 0) {
      alert('Please enter a valid positive price.');
      return;
    }
    if (selectedQuote) {
      adjustQuote(selectedQuote.id, newPrice, adminNotes);
      setSelectedQuoteId(null);
    }
  };

  // Submit to print management system
  const submitSelectedQuoteToPrintlogic = async (orderStatus) => {
    if (selectedQuote && !isSubmitting) {
      setIsSubmitting(true); // Prevent double-clicks
      try {
        const result = await submitToPrintSystem(selectedQuote.id, orderStatus);
        if (result.success) {
          // Extract order number - prioritize the response data
          const orderNumber = result.orderId || 'Unknown';
          console.log('Printlogic submission successful, order number:', orderNumber); // Debug log
          setSuccessModalData({
            orderNumber: orderNumber,
            quoteName: getQuoteName(selectedQuote)
          });
          setShowSuccessModal(true);
          setSelectedQuoteId(null);
        } else {
          // Handle submission in progress scenario with special UX
          if (result.submission_in_progress) {
            showNotification(`⏳ ${result.message} Please refresh the page manually to check status.`, 'info');
          } else {
            showNotification(`✗ ${result.message}`, 'error');
          }
          // Don't close the panel so user can see the message and take action
        }
      } catch (error) {
        showNotification(`✗ Failed to submit to Printlogic: ${error.message}`, 'error');
      } finally {
        setIsSubmitting(false); // Re-enable button
        setPendingPrintlogicAction(null);
      }
    }
  };

  const handleSubmitToPrintSystem = async () => {
    if (selectedQuote && !isSubmitting) {
      setPendingPrintlogicAction('submit');
    }
  };

  // Combined approve and submit to print system
  const approveAndSubmitSelectedQuoteToPrintlogic = async (orderStatus) => {
    if (selectedQuote && !isSubmitting) {
      setIsSubmitting(true); // Prevent double-clicks
      try {
        const result = await approveAndSubmitToPrintSystem(selectedQuote.id, adminNotes, orderStatus);
        if (result.success) {
          // Extract order number - prioritize the response data
          const orderNumber = result.orderId || 'Unknown';
          console.log('Printlogic approve and submit successful, order number:', orderNumber); // Debug log
          setSuccessModalData({
            orderNumber: orderNumber,
            quoteName: getQuoteName(selectedQuote)
          });
          setShowSuccessModal(true);
          setSelectedQuoteId(null);
        } else {
          // Handle partial success scenarios and submission in progress
          if (result.submission_in_progress) {
            showNotification(`⏳ ${result.message} Please refresh the page manually to check status.`, 'info');
          } else if (result.approvalSucceeded && !result.submissionSucceeded) {
            showNotification(`⚠ ${result.message}`, 'error');
          } else {
            showNotification(`✗ ${result.message}`, 'error');
          }
          // Don't close the panel so user can see the error and take action
        }
      } catch (error) {
        showNotification(`✗ Failed to approve and submit: ${error.message}`, 'error');
      } finally {
        setIsSubmitting(false); // Re-enable button
        setPendingPrintlogicAction(null);
      }
    }
  };

  const handleApproveAndSubmit = async () => {
    if (selectedQuote && !isSubmitting) {
      setPendingPrintlogicAction('approve-and-submit');
    }
  };

  const handleConfirmPrintlogicStatus = (orderStatus) => {
    if (pendingPrintlogicAction === 'approve-and-submit') {
      approveAndSubmitSelectedQuoteToPrintlogic(orderStatus);
      return;
    }
    submitSelectedQuoteToPrintlogic(orderStatus);
  };

  // Handle Printlogic customer selection
  const handlePrintlogicCustomerSelected = async (customer) => {
    if (!selectedQuote) return;

    try {
      // Auto-fill clientName with the customer name (user can edit it)
      const clientName = selectedQuote.clientName || customer.customer_name;

      // Save immediately to database
      const result = await updateQuotePrintlogicCustomer(
        selectedQuote.id,
        customer.customer_id,
        customer.customer_name,
        clientName
      );

      if (result.success) {
        setSelectedPrintlogicCustomer(customer);
        setShowPrintlogicCustomerModal(false);
        showNotification('✓ Customer assigned successfully', 'success');
      } else {
        showNotification(`✗ Failed to assign customer: ${result.message}`, 'error');
      }
    } catch (error) {
      showNotification(`✗ Error assigning customer: ${error.message}`, 'error');
    }
  };

  // Helper function to check if quote has valid Printlogic customer
  const hasValidPrintlogicCustomer = (quote) => {
    if (!quote) return false;
    const customerId = quote.printlogic_customer_id;
    // Invalid if null, undefined, empty string, or "N/A"
    return customerId && customerId.trim() && customerId !== 'N/A' && customerId !== 'NA';
  };

  // Identify quotes already converted/submitted into orders
  const isConvertedOrder = (quote) => {
    if (!quote) return false;
    return Boolean(quote.printlogic_order_number || quote.printlogic_order_status);
  };

  // --- End Action Handlers ---

  const DeliveryInfoDisplay = ({ deliveryInfo }) => {
    if (!deliveryInfo) return null;

    const { option, cost, boxesNeeded, estimatedWeight, hasOversizedItems, address } = deliveryInfo;

    return (
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-lg mb-2">Delivery Information</h3>

        <div className="space-y-2">
          <div>
            <span className="font-medium">Delivery Method:</span> {option === 'collection'
              ? 'Collection (Free)'
              : option === 'express'
                ? 'Express Delivery'
                : 'Standard Delivery'}
          </div>

          {option !== 'collection' && (
            <>
              <div>
                <span className="font-medium">Delivery Cost:</span> £{cost.toFixed(2)}
              </div>

              <div>
                <span className="font-medium">Estimated Weight:</span> {estimatedWeight}kg
                {boxesNeeded > 0 && <span> ({boxesNeeded} {boxesNeeded === 1 ? 'box' : 'boxes'})</span>}
              </div>

              {hasOversizedItems && (
                <div className="text-amber-700">
                  <span className="font-medium">Oversized Items:</span> Yes (includes £20 surcharge)
                </div>
              )}

              {address && (
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <div className="font-medium mb-1">Delivery Address:</div>
                  {typeof address === 'string' ? (
                    <div className="text-sm whitespace-pre-line">
                      {address.trim()}
                    </div>
                  ) : (
                    <div className="text-sm">
                      {address.line1}<br />
                      {address.line2 && <>{address.line2}<br /></>}
                      {address.city}{address.city && address.postcode ? ', ' : ''}{address.postcode}
                    </div>
                  )}

                  {typeof address === 'object' && address?.instructions && (
                    <div className="mt-2">
                      <span className="font-medium">Instructions: </span>
                      <span className="text-sm">{address.instructions}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg border-l-4 ${notification.type === 'success'
          ? 'bg-green-50 border-green-500 text-green-800'
          : notification.type === 'info'
            ? 'bg-blue-50 border-blue-500 text-blue-800'
            : 'bg-red-50 border-red-500 text-red-800'
          }`}>
          <div className="flex items-center justify-between">
            <p className="font-medium">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Filter bar: status + source (format handled by top selector) */}
      <div className="mb-4 sticky top-0 z-20 bg-white bg-opacity-95 backdrop-blur pt-2 pb-3">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex border-b border-gray-200 overflow-x-auto grow">
            {['pending', 'adjusted', 'approved', 'rejected', 'processing', 'completed', 'all'].map((status) => (
              <button
                key={status}
                onClick={() => setActiveFilter(status)}
                className={`px-3 py-2 font-medium text-sm whitespace-nowrap ${activeFilter === status
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {status === 'processing' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                {activeFilter === status && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-gray-100">
                    {quotesTotal || quotes.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex flex-col items-start sm:items-center gap-1">
            <div className="flex bg-gray-100 rounded-full p-1 shadow-inner" aria-label="Filter by source">
              {[
                { value: 'all', label: 'All' },
                { value: 'external', label: 'External Clients' },
                { value: 'internal', label: 'Internal Team' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setSourceFilter(value)}
                  aria-pressed={sourceFilter === value}
                  className={`px-3 h-9 text-sm font-semibold rounded-full transition whitespace-nowrap ${sourceFilter === value
                    ? 'bg-[#FAFA00] text-black shadow-sm'
                    : 'text-gray-700 hover:bg-white'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`grid gap-6 ${selectedQuote ? 'grid-cols-1 lg:grid-cols-8' : 'grid-cols-1'}`}>
        {/* Quote List */}
        <div
          className={selectedQuote ? 'lg:col-span-5 col-span-1' : 'col-span-1'}
          onClick={(e) => {
            // Close quote details when clicking on empty table area (desktop only)
            if (selectedQuote && window.innerWidth >= 1024 &&
              (e.target.tagName === 'TD' || e.target.tagName === 'TH' || e.target.classList.contains('quote-table-container'))) {
              // Don't close if clicking on interactive elements
              if (!e.target.closest('button') && !e.target.closest('tr[data-quote-id]')) {
                setSelectedQuoteId(null);
              }
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-md overflow-hidden quote-table-container">
            <div className="px-4 py-3 text-sm text-gray-600 border-b border-gray-200">
              Showing {quotes.length} of {quotesTotal || quotes.length} matching quotes
            </div>
            {/* Single scroll container for both vertical and horizontal scrolling */}
            <div
              ref={tableScrollRef}
              className="overflow-auto"
              style={{ maxHeight: tableMaxHeight }}
            >
                <table key={`table-${activeFilter}-${formatFilter}-${sourceFilter}`} className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quote / Job Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Summary
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredQuotes.length > 0 ? (
                      filteredQuotes.map((quote) => {
                        const quoteType = getQuoteType(quote);
                        return (
                          <tr
                            key={quote.id}
                            data-quote-id={quote.id}
                            className={`hover:bg-gray-50 cursor-pointer ${selectedQuote?.id === quote.id ? 'bg-blue-50' : ''}`}
                            onClick={() => handleSelectQuote(quote)}
                          >
                            {/* Type Cell */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(quoteType)}`}>
                                {quoteType}
                              </span>
                            </td>
                            {/* Due Date Cell */}
                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                              {(() => {
                                const dueDateInfo = formatDueDate(quote.dateRequired, quote.status);
                                return (
                                  <span className={`${dueDateInfo.textStyle} text-center text-sm font-semibold`}>
                                    {dueDateInfo.text}
                                  </span>
                                );
                              })()}
                            </td>
                            {/* Quote Name Cell */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{getQuoteName(quote)}</div>
                                  <div className="text-xs text-gray-500">
                                    {quote.printlogic_order_number
                                      ? `Order: ${quote.printlogic_order_number}`
                                      : `ID: ${quote.id?.substring(0, 8)}...`
                                    }
                                  </div>
                                </div>
                                {quote.printlogic_order_number && (
                                  <div className="flex-shrink-0">
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      Submitted
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            {/* Customer Cell */}
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                              {getClientDisplayName(quote)}
                            </td>
                            {/* Summary Cell */}
                            <td className="px-4 py-4 text-sm text-gray-700">
                              {getQuoteSummary(quote)}
                            </td>
                            {/* Price Cell */}
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <div className="text-sm font-medium text-gray-900">
                                £{(quote.totalPrice || quote.price || 0).toFixed(2)}
                              </div>
                              {quote.status === 'adjusted' && quote.originalPrice != null && (
                                <div className="text-xs text-gray-500 line-through">
                                  £{quote.originalPrice.toFixed(2)}
                                </div>
                              )}
                            </td>
                            {/* Status Cell */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(quote.status)}`}>
                                {quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1) || 'Unknown'}
                              </span>
                            </td>
                            {/* Date Cell */}
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(quote.createdAt)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                          No quotes found matching the current filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
            </div>
          </div>
          {quotesHasMore && (
            <div className="mt-4 text-center">
              <button
                onClick={loadMoreQuotes}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {isLoading ? 'Loading...' : 'Load more quotes'}
              </button>
            </div>
          )}
        </div>

        {/* Quote Details / Actions Panel */}
        {selectedQuote && (
          <div className="lg:col-span-3 order-first lg:order-last">
            <div className="bg-white rounded-lg shadow-md p-6 overflow-y-auto max-h-[calc(100vh-10rem)]">
              <h2 className="text-lg font-semibold mb-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span>Quote Review</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadge(getQuoteType(selectedQuote))}`}>
                    {getQuoteType(selectedQuote)}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedQuoteId(null)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close quote details"
                >
                  ×
                </button>
              </h2>

              <div className="mb-4 space-y-1 text-sm">
                <div><strong>Quote Name:</strong> {getQuoteName(selectedQuote)}</div>
                <div><strong>Client:</strong> {selectedQuote.clientName || selectedQuote.customerName || selectedQuote.username || 'N/A'}</div>
                <div><strong>Date:</strong> {formatDate(selectedQuote.createdAt)}</div>
                <div><strong>Status:</strong>
                  <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(selectedQuote.status)}`}>
                    {selectedQuote.status?.charAt(0).toUpperCase() + selectedQuote.status?.slice(1) || 'Unknown'}
                  </span>
                </div>
                <div className="text-xs text-gray-600"><strong>Created by:</strong> {selectedQuote.username || 'Unknown'}</div>
              </div>

              {/* Products List */}
              {selectedQuote.items && selectedQuote.items.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-700 mb-1">Products ({selectedQuote.items.length})</h3>
                  <div className="mt-1 border border-gray-200 rounded"> {/* Removed height constraint - now dynamically sized */}
                    {selectedQuote.items.map((item, index) => {
                      const itemType = item.paperStock ? 'Small Format' : item.material ? 'Large Format' : 'Unknown';
                      const processes = itemType === 'Large Format'
                        ? getLargeFormatProcessNames(item)
                        : item.processes || [];
                      const isCutVinyl = isCutVinylJob(selectedQuote);
                      console.log(`Admin Panel - Item ${index} Data:`, JSON.stringify(item, null, 2)); // Add this line
                      return (
                        <div
                          key={item.id || index}
                          className={`p-2 text-sm ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} ${index !== selectedQuote.items.length - 1 ? 'border-b border-gray-200' : ''
                            }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">{item.jobName || `Product ${index + 1}`}</div>
                              {/* Display relevant details based on type */}
                              {itemType === 'Small Format' && (
                                <>
                                  <div className="text-gray-600">Paper: {item.paperStockName || item.paperStock || 'N/A'}</div>
                                  <div className="text-gray-600">Size: {item.dimensions || `${item.width}x${item.height}mm`}</div>
                                  {item.isBooklet && <div className="text-gray-600">Pages: {item.pageCount}</div>}
                                  <div className="text-gray-600">Qty: {item.quantity}</div>
                                  {(() => {
                                    const sidesBase = item.isDoubleSided ? 'Double' : 'Single';
                                    const coverSides = item.coverIsDoubleSided;
                                    let sidesLabel = sidesBase;
                                    if (item.isDoubleSided === true && coverSides === false) {
                                      sidesLabel += ' (cover single)';
                                    } else if (item.isDoubleSided === false && coverSides === true) {
                                      sidesLabel += ' (cover double)';
                                    }
                                    return <div className="text-gray-600">Sides: {sidesLabel}</div>;
                                  })()}
                                  {(() => {
                                    const formatColour = (value) => {
                                      if (!value) return null;
                                      if (value === 'full_colour' || value === 'full_color') return 'Full Colour';
                                      if (value === 'black_white' || value === 'mono') return 'B&W';
                                      return value;
                                    };
                                    const innerColour = formatColour(item.colours) || 'N/A';
                                    const coverColourRaw = item.coverColours || item.cover_colours;
                                    const coverColourFormatted = formatColour(coverColourRaw);
                                    let colourLabel = innerColour;
                                    if (
                                      coverColourFormatted &&
                                      coverColourFormatted !== innerColour &&
                                      coverColourFormatted !== 'N/A'
                                    ) {
                                      colourLabel += ` (cover ${coverColourFormatted.toLowerCase()})`;
                                    }
                                    return <div className="text-gray-600">Colour: {colourLabel}</div>;
                                  })()}
                                </>
                              )}
                              {itemType === 'Large Format' && (
                                <div className="text-gray-600">
                                  {item.pieces && item.pieces.length > 0 ? (
                                    <>
                                      <div>Pieces: {item.pieces.length}</div>
                                      <div>
                                        Materials:{' '}
                                        {item.materials?.map((m) => m.name).join(', ') ||
                                          item.materialNames ||
                                          'N/A'}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div>Material: {item.material || item.materialNames || 'N/A'}</div>
                                      <div>Size: {item.dimensions || `${item.width}x${item.height}mm`}</div>
                                      <div>Qty: {item.quantity}</div>
                                    </>
                                  )}
                                </div>
                              )}
                              {processes.length > 0 && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  Finishing: {processes.map((process, idx) => {
                                    const isLaminate = process.toLowerCase().includes('laminate');
                                    const processId = isLaminate ?
                                      process.toLowerCase().includes('duplex') ? 'duplex-laminate' : 'laminate'
                                      : '';
                                    const finishingOpts = item.finishingOptions || item.finishing_options;
                                    const laminateType = isLaminate && finishingOpts &&
                                      finishingOpts[processId] &&
                                      finishingOpts[processId]['laminateType'] ?
                                      ` (${getLaminateTypeName(finishingOpts[processId]['laminateType'])})`
                                      : '';

                                    return `${idx > 0 ? ', ' : ''}${process}${laminateType}`;
                                  }).join('')}
                                </div>
                              )}
                            </div>
                            <div className="font-medium whitespace-nowrap pl-2">£{item.price?.toFixed(2) || '0.00'}</div>
                          </div>
                          {/* Add Breakdown details for each item */}
                          {item.breakdown && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-sm text-gray-600 hover:text-blue-600 py-1 px-2 bg-gray-50 rounded border hover:bg-gray-100 transition-colors">
                                View Price Breakdown
                              </summary>
                              <div className="mt-2 p-3 bg-gray-50 rounded border-l-4 border-blue-200">
                                <BreakdownDisplay
                                  item={item}
                                  breakdown={item.breakdown}
                                  itemType={isCutVinyl ? 'cut-vinyl' : itemType}
                                  pricingTier={item.breakdown.pricing_tier_final_result || item.breakdown.pricing_tier || 'retail'}
                                />
                              </div>
                            </details>
                          )}

                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedQuote.deliveryInfo && (
                <DeliveryInfoDisplay deliveryInfo={selectedQuote.deliveryInfo} />
              )}

              {/* Total Price */}
              <div className="mb-4">
                <h3 className="font-medium text-gray-700">Total Price</h3>
                <div className="flex flex-col">
                  {selectedQuote.deliveryInfo && selectedQuote.deliveryInfo.cost > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Products:</span>
                        <span>£{(selectedQuote.totalProductsPrice || selectedQuote.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Delivery:</span>
                        <span>£{selectedQuote.deliveryInfo.cost.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t border-gray-200">
                    <span>Total:</span>
                    {/* Calculate the total correctly, including delivery cost */}
                    <span>£{(
                      selectedQuote.deliveryInfo && selectedQuote.deliveryInfo.cost > 0
                        ? (selectedQuote.totalProductsPrice || selectedQuote.price || 0) + selectedQuote.deliveryInfo.cost
                        : (selectedQuote.totalPrice || selectedQuote.price || 0)
                    ).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {isAdmin() && <div className="mb-4 border-t pt-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleGenerateQuotation}
                    disabled={isGeneratingQuotation}
                    className="inline-flex items-center px-3 py-2 border border-indigo-300 rounded-md text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2M6 3h12a2 2 0 012 2v4H4V5a2 2 0 012-2z" />
                    </svg>
                    {isGeneratingQuotation ? 'Generating quotation...' : 'Generate quotation PDF'}
                  </button>
                  <button
                    onClick={() => setShowQuotationOptions(!showQuotationOptions)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Options
                  </button>
                </div>
                {showQuotationOptions && (
                  <div className="mt-3 space-y-3 bg-gray-50 border border-gray-200 rounded-md p-3">
                    <div>
                      <label htmlFor="quotation-recipient" className="block text-xs font-medium text-gray-700 mb-1">Address to</label>
                      <input
                        id="quotation-recipient"
                        type="text"
                        value={quotationRecipient}
                        onChange={(e) => setQuotationRecipient(e.target.value)}
                        placeholder="Customer name"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="quotation-signatory" className="block text-xs font-medium text-gray-700 mb-1">Signed by</label>
                      <input
                        id="quotation-signatory"
                        type="text"
                        value={quotationSignatory}
                        onChange={(e) => setQuotationSignatory(e.target.value)}
                        placeholder="Defaults to the quote's account manager"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500">Overrides the &quot;To&quot; line and the name after &quot;Yours sincerely&quot;.</p>
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">Creates a customer quotation with prices excluding VAT.</p>
              </div>}

              {/* File Review & Job Folder Management - Show for all statuses except completed */}
              {!isCompletedStatus(selectedQuote.status) && (
                <div className="mb-4 border-t pt-4">
                  <h3 className="font-medium text-gray-700 mb-3">
                    {selectedQuote.status === 'pending' ? 'Review Uploaded Files' : 'Job Folder & Printroom'}
                  </h3>

                  {/* Debug Info */}
                  <div className="mb-2 text-xs text-gray-500 bg-gray-100 p-2 rounded">
                    Status: {selectedQuote.status} | PrintLogic Order: {selectedQuote.printlogic_order_number || 'None'} | Job ID: {selectedQuote.id}
                  </div>

                  {/* Folder View Button - Always available */}
                  <div className="mb-3">
                    <button
                      onClick={() => setShowFolderView(true)}
                      className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${selectedQuote.status === 'pending'
                        ? 'border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100'
                        : 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                        }`}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v0M8 5v.01M16 5v.01" />
                      </svg>
                      {selectedQuote.status === 'pending' ? 'Review Uploaded Files' : 'View Job Folder'}
                    </button>
                    {selectedQuote.status === 'pending' && (
                      <p className="text-sm text-gray-600 mt-1">
                        Review any files uploaded by the customer before approving this quote.
                      </p>
                    )}
                  </div>

                  {/* Printroom Control - Only for approved/processing quotes */}
                  {selectedQuote.status !== 'pending' && (
                    <PrintroomControl
                      jobId={selectedQuote.id}
                      orderNumber={selectedQuote.printlogic_order_number || 'TEST-ORDER'}
                      quoteName={getQuoteName(selectedQuote)}
                      onNotificationSent={(result) => {
                        if (result.success) {
                          showNotification(`✓ Job sent to printroom successfully! Order: ${selectedQuote.printlogic_order_number || 'TEST-ORDER'}`, 'success');
                        } else {
                          showNotification(`✗ Failed to send to printroom: ${result.message}`, 'error');
                        }
                      }}
                    />
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedQuote.notes && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-700">Customer Notes</h3>
                  <p className="text-sm bg-gray-50 p-2 rounded border border-gray-200">{selectedQuote.notes}</p>
                </div>
              )}

              {/* --- Printlogic Customer Assignment Section --- */}
              {selectedQuote.status === 'pending' && (
                <div className="space-y-4 border-t pt-4 bg-blue-50 border-l-4 border-l-blue-400 p-4 rounded">
                  <div>
                    <label className="block text-sm font-semibold text-blue-900 mb-2">
                      Printlogic Customer Assignment
                    </label>
                    {selectedPrintlogicCustomer && hasValidPrintlogicCustomer(selectedQuote) ? (
                      <div className="flex items-center justify-between bg-white p-3 rounded border border-blue-200">
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">Printlogic Account:</span> {selectedQuote.printlogic_account_name || selectedPrintlogicCustomer.customer_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500">
                            ID: {selectedQuote.printlogic_customer_id || selectedPrintlogicCustomer.customer_id}
                          </p>
                          {selectedQuote.clientName && selectedQuote.clientName !== selectedQuote.printlogic_account_name && (
                            <p className="text-sm text-gray-700 mt-2">
                              <span className="font-medium">Client Name:</span> {selectedQuote.clientName}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setShowPrintlogicCustomerModal(true)}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-yellow-50 p-3 rounded border border-yellow-200">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-yellow-800">No customer assigned</span>
                        </div>
                        <button
                          onClick={() => setShowPrintlogicCustomerModal(true)}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                        >
                          Assign Customer
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* --- Action Section --- */}
              {/* (Pending Status Actions) */}
              {selectedQuote.status === 'pending' && (
                <div className="space-y-4 border-t pt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adjust Price (Optional)
                    </label>
                    <input
                      type="number"
                      value={adjustedPrice}
                      onChange={(e) => setAdjustedPrice(e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder={`Current: £${(selectedQuote.totalPrice || selectedQuote.price || 0).toFixed(2)}`}
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Manager Notes
                    </label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="w-full p-2 border rounded"
                      rows="3"
                      placeholder="Enter notes for customer (required for rejection or adjustment)"
                    ></textarea>
                  </div>
                  <div className="space-y-3">
                    {/* Check if already submitted to Printlogic */}
                    {selectedQuote.printlogic_order_number ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center text-blue-800">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Already submitted to Printlogic
                        </div>
                        <div className="font-mono font-medium text-blue-900 mt-1">
                          Order: {selectedQuote.printlogic_order_number}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* First row: Primary actions */}
                        <div className="flex gap-2">
                          <button
                            className={`flex-1 px-3 py-2 text-sm rounded font-medium ${hasValidPrintlogicCustomer(selectedQuote) && !isSubmitting
                              ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer'
                              : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              }`}
                            onClick={handleApproveAndSubmit}
                            disabled={!hasValidPrintlogicCustomer(selectedQuote) || isSubmitting}
                            title={!hasValidPrintlogicCustomer(selectedQuote) ? 'Please assign a Printlogic customer before submitting' : isSubmitting ? 'Submitting...' : ''}
                          >
                            {isSubmitting ? 'Submitting...' : 'Approve & Submit to PrintLogic'}
                          </button>
                        </div>
                        {!hasValidPrintlogicCustomer(selectedQuote) && (
                          <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200 flex items-center">
                            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Cannot submit without a valid Printlogic customer ID
                          </div>
                        )}
                      </>
                    )}
                    {/* Second row: Alternative actions - Always show for pending quotes */}
                    {!selectedQuote.printlogic_order_number && (
                      <div className="flex flex-wrap justify-between gap-2">
                        <button className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700" onClick={handleReject}>Reject</button>
                        <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300" onClick={handleAdjust} disabled={!adjustedPrice || parseFloat(adjustedPrice) === (selectedQuote.totalPrice || selectedQuote.price || 0)}>Adjust Price</button>
                        <button className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700" onClick={handleApprove}>Approve Only</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* (Adjusted Status Info) */}
              {selectedQuote.status === 'adjusted' && (
                <div className="bg-blue-50 p-3 rounded mb-4 text-sm text-blue-800 border border-blue-200">
                  This quote has been adjusted and is awaiting customer approval. Manager Notes: {selectedQuote.managerNotes || 'None'}
                </div>
              )}

              {/* (Approved Status Actions) */}
              {selectedQuote.status === 'approved' && (
                <div className="space-y-4 border-t pt-4">
                  <div className="bg-green-50 p-3 rounded text-sm text-green-800 border border-green-200">
                    This quote is approved. Manager Notes: {selectedQuote.managerNotes || 'None'}
                  </div>

                  {selectedQuote.printlogic_order_number ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                      <div className="flex items-center text-blue-800 mb-2">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Already submitted to Printlogic
                      </div>
                      <div className="font-mono font-semibold text-blue-900">
                        Order: {selectedQuote.printlogic_order_number}
                      </div>
                      {selectedQuote.printlogic_submitted_at && (
                        <div className="text-xs text-blue-600 mt-1">
                          Submitted: {new Date(selectedQuote.printlogic_submitted_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Print System Job ID (Optional)
                        </label>
                        <input
                          type="text"
                          value={printSystemId}
                          onChange={(e) => setPrintSystemId(e.target.value)}
                          className="w-full p-2 border rounded"
                          placeholder="Enter job ID from print system"
                        />
                      </div>
                      <button
                        className={`w-full px-4 py-2 rounded font-medium ${!isSubmitting
                          ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        }`}
                        onClick={handleSubmitToPrintSystem}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit to Print System'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* (Rejected/Processing Status Info) */}
              {(selectedQuote.status === 'rejected' || selectedQuote.status === 'processing') && (
                <div className={`p-3 rounded mb-4 text-sm border ${selectedQuote.status === 'rejected' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-purple-50 text-purple-800 border-purple-200'
                  }`}>
                  {selectedQuote.status === 'rejected'
                    ? `This quote was rejected. Reason: ${selectedQuote.managerNotes || 'None provided'}`
                    : `This job has been submitted (Order # ${selectedQuote.printlogic_order_number || 'N/A'}).`
                  }
                </div>
              )}

              {/* Close button */}
              <div className="mt-4 border-t pt-4">
                <button className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300" onClick={() => setSelectedQuoteId(null)}>
                  Close Details
                </button>
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Job Folder View Modal */}
      {selectedQuote && (
        <JobFolderView
          jobId={selectedQuote.id}
          quoteName={getQuoteName(selectedQuote)}
          isVisible={showFolderView}
          onClose={() => setShowFolderView(false)}
        />
      )}

      {/* Printlogic Success Modal */}
      <PrintlogicSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        orderNumber={successModalData.orderNumber}
        quoteName={successModalData.quoteName}
      />

      {/* Printlogic Customer Modal */}
      <PrintlogicCustomerModal
        isOpen={showPrintlogicCustomerModal}
        onClose={() => setShowPrintlogicCustomerModal(false)}
        onSelectCustomer={handlePrintlogicCustomerSelected}
      />

      <PrintlogicOrderStatusModal
        isOpen={Boolean(pendingPrintlogicAction)}
        onCancel={() => setPendingPrintlogicAction(null)}
        onConfirm={handleConfirmPrintlogicStatus}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default AdminPanel;
